import { type FormikHelpers } from 'formik';
import { omit } from 'lodash';
import { type NavigateFunction } from 'react-router-dom';

import { toast } from '@hivebrite/design-system/Toast';

import {
  axiosWithSyntaxHandlers as axios,
  formSerializerVisitor,
} from 'hb-react/shared/utils/axios';

import { MODE } from '../constants';
import { type Job, type FormValues, type ModeValues } from '../types';

export const onSubmit =
  ({
    currentUserId,
    navigate,
    mode,
    networkId,
    topicId,
    onSuccess,
  }: {
    currentUserId?: number;
    mode: ModeValues;
    navigate?: NavigateFunction;
    networkId: number;
    onSuccess?: (companyId?: number) => void;
    topicId?: number;
  }) =>
  (
    values: FormValues,
    { setFieldValue, setSubmitting }: FormikHelpers<FormValues>,
  ) => {
    const { id: jobId, token } = values;

    let rootKey = values.type_param;

    let data = {};

    let url = '';

    if (mode === MODE.BO) {
      url = topicId
        ? Routes.backoffice_network_topic_job_path(networkId, topicId, jobId)
        : Routes.backoffice_network_opportunity_path(networkId, jobId);
    } else if (mode === MODE.FO && currentUserId) {
      rootKey = 'user_request';

      url = Routes.user_business_opportunities_request_path(
        currentUserId,
        jobId,
      );
    } else if (mode === MODE.PO) {
      rootKey = 'external_recruiter_request';

      data = {
        captcha: values.captcha,
        captcha_key: values.captcha_key,
      };

      url = Routes.networks_recruiter_job_path(jobId, { token });
    }

    setFieldValue('draft', false);

    data = {
      [rootKey]: omit(values, ['captcha', 'captcha_key', 'pay_period']),
      ...data,
    };

    axios
      .patchForm(url, data, {
        formSerializer: { visitor: formSerializerVisitor },
      })
      .then(({ data }: { data: Job }) => {
        if (mode === MODE.BO) {
          toast.success(s__('JobForm|Successfully saved'));

          window.location.replace(
            topicId
              ? Routes.edit_backoffice_network_topic_job_path(
                  networkId,
                  topicId,
                  jobId,
                )
              : Routes.edit_backoffice_network_opportunity_path(
                  networkId,
                  jobId,
                ),
          );
        } else if (mode === MODE.FO) {
          toast.success(s__('JobForm|Successfully saved'));
          if (onSuccess) {
            onSuccess(data?.company_id);
          }

          if (navigate) {
            navigate(Routes.opportunity_path({ id: jobId }));
          }
        } else if (mode === MODE.PO) {
          window.location.assign(
            Routes.edit_networks_recruiter_job_path(jobId, {
              success: true,
              token,
            }),
          );
        }
      })
      .catch((err) => {
        setFieldValue('draft', true);

        toast.error(
          mode === MODE.PO && err?.response?.status === 422
            ? s__('JobForm|CAPTCHA code does not match')
            : s__('JobForm|An error has occurred'),
        );
      })
      .finally(() => setSubmitting(false));
  };
